import React, { useCallback, useMemo } from 'react'
import { Grid } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import CheckboxWithNote from 'components/common/inputs/CheckboxWithNote'
import CategorySubtitle from './CategorySubtitle'

const ObservationTrackingCategory = ({ categoryValues = {}, onChange, viewMode }) => {
  const { t } = useTranslation()

  const normalizeCheckboxValue = useCallback(value => {
    if (!value) {
      return { isChecked: false, note: '' }
    }

    if (typeof value === 'string') {
      return { isChecked: true, note: value }
    }

    if (typeof value === 'object') {
      return { isChecked: !!value.isChecked, note: value.note || '' }
    }

    return { isChecked: false, note: '' }
  }, [])

  const fields = useMemo(
    () => [
      { key: 'teethEruption', label: t('dialogs.patientBrief.categories.observationTracking.teethEruption') },
      { key: 'spacingCrowding', label: t('dialogs.patientBrief.categories.observationTracking.spacingCrowding') },
      {
        key: 'oralHealthImprovements',
        label: t('dialogs.patientBrief.categories.observationTracking.oralHealthImprovements')
      },
      { key: 'biteChange', label: t('dialogs.patientBrief.categories.observationTracking.biteChange') },
      { key: 'other', label: t('dialogs.patientBrief.categories.observationTracking.other') }
    ],
    [t]
  )

  const handleFieldChanged = useCallback(
    (fieldKey, changes) => {
      const currentValue = normalizeCheckboxValue(categoryValues[fieldKey])

      onChange({
        [fieldKey]: {
          ...currentValue,
          ...changes
        }
      })
    },
    [categoryValues, normalizeCheckboxValue, onChange]
  )

  if (viewMode) {
    return (
      <Grid container direction="column" spacing={1}>
        {fields
          .map(field => ({
            ...field,
            value: normalizeCheckboxValue(categoryValues[field.key])
          }))
          .filter(field => field.value.isChecked)
          .map(field => (
            <Grid item key={`observation-view-${field.key}`}>
              <CategorySubtitle viewMode>{field.label}</CategorySubtitle>
              {field.value.note && <DazzedParagraph14 strong>{field.value.note}</DazzedParagraph14>}
            </Grid>
          ))}
      </Grid>
    )
  }

  return (
    <Grid container direction="column" spacing={1}>
      {fields.map(field => {
        const value = normalizeCheckboxValue(categoryValues[field.key])

        return (
          <Grid item key={`observation-field-${field.key}`}>
            <CheckboxWithNote
              label={field.label}
              isChecked={value.isChecked}
              setIsChecked={isChecked => handleFieldChanged(field.key, { isChecked })}
              note={value.note}
              setNote={note => handleFieldChanged(field.key, { note })}
              noteInputPlaceholder={t('dialogs.patientBrief.actions.customNote')}
            />
          </Grid>
        )
      })}
    </Grid>
  )
}

export default ObservationTrackingCategory
